// src/components/DataDocumentationPage.tsx - Data Documentation using standard app styling
import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '../constants';
import PageContainer from './PageContainer';
import './DataDocumentationPage.css';

interface DataDocumentationPageProps {
  onBack: () => void;
}

interface Field {
  field_name: string;
  label: string;
  type: string;
  total_documents: number;
  fill_rate: number;
}

interface DataSource {
  data_source: string;
  total_records: number;
  total_fields: number;
  fields: Field[];
}

interface ApiResponse {
  success: boolean;
  data_sources: {
    people: DataSource;
    company: DataSource;
  };
  generated_at: string;
}

const DataDocumentationPage: React.FC<DataDocumentationPageProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [selectedDataSource, setSelectedDataSource] = useState<'people' | 'company'>('people');

  useEffect(() => {
    const fetchDataDocs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE_URL}/api/public/data-documentation?data_source=all`);
        
        if (!response.ok) {
          throw new Error(`Failed to load data documentation: ${response.statusText}`);
        }
        
        const result: ApiResponse = await response.json();
        setData(result);
      } catch (err) {
        console.error('Error fetching data documentation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data documentation');
      } finally {
        setLoading(false);
      }
    };

    fetchDataDocs();
  }, []);

  const currentDataSource = data?.data_sources[selectedDataSource];

  return (
    <PageContainer 
      title="Data Documentation" 
      subtitle="Comprehensive overview of available data fields, coverage statistics, and fill rates"
      onBack={onBack}
      maxWidth="1200px"
    >
      {loading ? (
        <div className="data-docs-loading">
          <Loader2 size={32} className="spinning" />
          <p>Loading data documentation...</p>
        </div>
      ) : error ? (
        <div className="data-docs-error">
          <AlertTriangle size={32} />
          <h3>Error Loading Documentation</h3>
          <p>{error}</p>
        </div>
      ) : data && currentDataSource ? (
        <>
          {/* Data Source Tabs */}
          <div className="data-docs-tabs">
            <button
              className={`data-docs-tab ${selectedDataSource === 'people' ? 'active' : ''}`}
              onClick={() => setSelectedDataSource('people')}
            >
              People Data ({data.data_sources.people.total_records.toLocaleString()} records)
            </button>
            <button
              className={`data-docs-tab ${selectedDataSource === 'company' ? 'active' : ''}`}
              onClick={() => setSelectedDataSource('company')}
            >
              Company Data ({data.data_sources.company.total_records.toLocaleString()} records)
            </button>
          </div>

          {/* Summary Stats */}
          <div className="data-docs-stats">
            <div className="stat-item">
              <div className="stat-label">Total Records</div>
              <div className="stat-value">{currentDataSource.total_records.toLocaleString()}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Available Fields</div>
              <div className="stat-value">{currentDataSource.total_fields}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Average Fill Rate</div>
              <div className="stat-value">
                {Math.round(
                  currentDataSource.fields.reduce((sum, f) => sum + f.fill_rate, 0) / 
                  currentDataSource.fields.length
                )}%
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="data-docs-table-wrapper">
            <table className="data-docs-table">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Type</th>
                  <th>Documents</th>
                  <th>Fill Rate</th>
                </tr>
              </thead>
              <tbody>
                {currentDataSource.fields.map((field) => (
                  <tr key={field.field_name}>
                    <td className="field-name">{field.label}</td>
                    <td>
                      <span className="type-badge">{field.type}</span>
                    </td>
                    <td className="numeric">{field.total_documents.toLocaleString()}</td>
                    <td className="numeric">{field.fill_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer Info */}
          <div className="data-docs-footer">
            <p>Last updated: {new Date(data.generated_at).toLocaleString()}</p>
          </div>
        </>
      ) : null}
    </PageContainer>
  );
};

export default DataDocumentationPage;
